'use client';

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { validateFileSize, MAX_FILE_SIZE_MB, validateFileType } from '../../../../../utils/validationUtils';

export default function SellerStep7BusinessDetails() {
  const { register, watch, setValue, formState: { errors, touchedFields, isSubmitted } } = useFormContext();
  const { translate } = useLanguage();
  const [previewTradeLicense, setPreviewTradeLicense] = useState('');
  const [previewTrnCertification, setPreviewTrnCertification] = useState('');
  const [fileSizeError, setFileSizeError] = useState({ tradeLicense: '', trnCertification: '' });

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!validateFileType(file)) {
        const errorMsg = translate('auth.signup.fileFormats');
        setFileSizeError(prev => ({ ...prev, [type]: errorMsg }));
        e.target.value = '';
        if (type === 'tradeLicense') {
          setPreviewTradeLicense('');
        } else {
          setPreviewTrnCertification('');
        }
        return;
      }

      // Validate file size (5MB limit)
      if (!validateFileSize(file)) {
        const errorMsg = translate('auth.signup.validation.fileSizeError') || `File size must not exceed ${MAX_FILE_SIZE_MB}MB`;
        setFileSizeError(prev => ({ ...prev, [type]: errorMsg }));
        // Clear the file input
        e.target.value = '';
        if (type === 'tradeLicense') {
          setPreviewTradeLicense('');
        } else {
          setPreviewTrnCertification('');
        }
        return;
      }

      // Clear any previous error
      setFileSizeError(prev => ({ ...prev, [type]: '' }));

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'tradeLicense') {
          setPreviewTradeLicense(reader.result);
        } else {
          setPreviewTrnCertification(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (type) => {
    if (type === 'tradeLicense') {
      setValue('tradeLicenseImage', null);
      setPreviewTradeLicense('');
      setFileSizeError(prev => ({ ...prev, tradeLicense: '' }));
      const input = document.getElementById('tradeLicenseImage');
      if (input) input.value = '';
    } else {
      setValue('trnCertificationImage', null);
      setPreviewTrnCertification('');
      setFileSizeError(prev => ({ ...prev, trnCertification: '' }));
      const input = document.getElementById('trnCertificationImage');
      if (input) input.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className='text-center'>
        <h2 className="text-2xl font-bold text-white mb-2">
          {translate("auth.signup.businessDetails")}
        </h2>
        <p className="text-white">
          {translate("auth.signup.provideBusinessInformation")}
        </p>
      </div>

      <div className="space-y-4">
        {watch('accountType') !== 'individual' && (
          <>
            <div>
              <label htmlFor="businessName" className="block text-sm font-mediumtext-white mb-2">
                {translate("auth.signup.businessName")} <span className="text-red-500">*</span>
              </label>
              <input
                {...register('businessName')}
                id="businessName"
                type="text"
                placeholder={translate("auth.signup.businessNamePlaceholder")}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              {(touchedFields.businessName || isSubmitted) && errors.businessName && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors.businessName.message)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="businessRegistrationNumber" className="block text-sm font-mediumtext-white mb-2">
                {translate("auth.signup.businessRegNo")} <span className="text-red-500">*</span>
              </label>
              <input
                {...register('businessRegistrationNumber')}
                id="businessRegistrationNumber"
                type="text"
                placeholder={translate("auth.signup.businessRegNoPlaceholder")}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              {(touchedFields.businessRegistrationNumber || isSubmitted) && errors.businessRegistrationNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors.businessRegistrationNumber.message)}
                </p>
              )}
            </div>
          </>
        )}

        <div className="space-y-3">
          <label htmlFor="tradeLicenseImage" className="block text-sm font-mediumtext-white mb-2">
            {translate("auth.signup.tradeLicenseImage")}
          </label>
          <div className="relative">
            <input
              {...register('tradeLicenseImage')}
              id="tradeLicenseImage"
              type="file"

              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                handleFileChange(e, 'tradeLicense');
                register('tradeLicenseImage').onChange?.(e);
              }}
            />
            <label
              htmlFor="tradeLicenseImage"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer "
            >
              <span className="text-gray-500 truncate mr-2">
                {watch('tradeLicenseImage')?.[0]?.name || translate("auth.signup.documentUpload")}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {watch('tradeLicenseImage')?.[0] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveFile('tradeLicense');
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <span className="bg-primary text-white px-3 py-1 rounded text-sm shrink-0">
                  {translate("auth.signup.browseFile")}
                </span>
              </div>
            </label>
          </div>
          {(touchedFields.tradeLicenseImage || isSubmitted) && !fileSizeError.tradeLicense && errors.tradeLicenseImage && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.tradeLicenseImage.message)}
            </p>
          )}
          {fileSizeError.tradeLicense && (
            <p className="text-red-500 text-sm mt-1">
              {fileSizeError.tradeLicense}
            </p>
          )}

          {previewTradeLicense && (
            <div className="mt-3 border-2 border-gray-200 rounded-lg overflow-hidden">
              <img
                src={previewTradeLicense || "/placeholder.svg"}
                alt={translate("auth.signup.tradeLicensePreview")}
                className="w-full h-auto max-h-48 object-cover"
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label htmlFor="trnCertificationImage" className="block text-sm font-mediumtext-white mb-2">
            {translate("auth.signup.trnCertificateImage")}
          </label>
          <div className="relative">
            <input
              {...register('trnCertificationImage')}
              id="trnCertificationImage"
              type="file"

              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                handleFileChange(e, 'trnCertification');
                register('trnCertificationImage').onChange?.(e);
              }}
            />
            <label
              htmlFor="trnCertificationImage"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer "
            >
              <span className="text-gray-500 truncate mr-2">
                {watch('trnCertificationImage')?.[0]?.name || translate("auth.signup.documentUpload")}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {watch('trnCertificationImage')?.[0] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveFile('trnCertification');
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <span className="bg-primary text-white px-3 py-1 rounded text-sm shrink-0">
                  {translate("auth.signup.browseFile")}
                </span>
              </div>
            </label>
          </div>
          {(touchedFields.trnCertificationImage || isSubmitted) && !fileSizeError.trnCertification && errors.trnCertificationImage && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.trnCertificationImage.message)}
            </p>
          )}
          {fileSizeError.trnCertification && (
            <p className="text-red-500 text-sm mt-1">
              {fileSizeError.trnCertification}
            </p>
          )}

          {previewTrnCertification && (
            <div className="mt-3 border-2 border-gray-200 rounded-lg overflow-hidden">
              <img
                src={previewTrnCertification || "/placeholder.svg"}
                alt={translate("auth.signup.trnCertificationPreview")}
                className="w-full h-auto max-h-48 object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
